import { Link } from "react-router-dom";
import { Building2, Rocket, Handshake, Landmark, ArrowUpRight } from "lucide-react";
import MarketingLayout from "../../components/marketing/MarketingLayout";

const SOLUTIONS = [
  { id: "enterprise", Icon: Building2, name: "Enterprise",
    tag: "For organizations of 500+ people",
    body: "Multi-layer sensing across projects, leadership and teams. Enterprise-grade privacy, custom domain, SSO on request.",
    bullets: ["Reality Gap sensing", "Executive intelligence dashboards", "Anonymized aggregation", "Custom SLA + support"],
  },
  { id: "startups", Icon: Rocket, name: "Startups",
    tag: "For fast-moving teams under 200",
    body: "Sense drift before it costs you your best people. Lightweight setup, delightful UX, upgrade only when you need it.",
    bullets: ["Weekly Growth pulse", "Adaptive assessment fatigue", "Founder-friendly pricing", "Slack-fast onboarding"],
  },
  { id: "agencies", Icon: Handshake, name: "Agencies & Consultancies",
    tag: "For client-delivery businesses",
    body: "Deploy SYNQ across client projects. Multi-tenant workspace, portable intelligence, whitelabel available.",
    bullets: ["Multi-tenant workspace", "Client-branded reports", "Rapid campaign launches", "Portable intelligence"],
  },
  { id: "government", Icon: Landmark, name: "Government & Public Sector",
    tag: "For departments and agencies",
    body: "Trust-first intelligence for public organizations. Systemic recommendations, transparent decision governance.",
    bullets: ["Data residency options", "Systemic (non-punitive) framing", "Audit-friendly export", "Policy Intelligence layer"],
  },
];

export default function SolutionsPage() {
  return (
    <MarketingLayout>
      <section className="relative overflow-hidden">
        <div aria-hidden className="absolute inset-0 -z-10">
          <div className="absolute -top-40 right-0 w-[700px] h-[700px] rounded-full bg-emerald-100 dark:bg-emerald-950/40 blur-3xl opacity-40" />
        </div>
        <div className="max-w-6xl mx-auto px-5 md:px-8 pt-20 md:pt-28 pb-16">
          <div className="font-ibm-plex text-[10px] tracking-widest uppercase text-blue-600 dark:text-blue-400 font-bold mb-4">Solutions</div>
          <h1 className="font-outfit text-5xl md:text-6xl font-black text-slate-900 dark:text-white leading-[1.05] max-w-4xl">
            One platform. Every organizational shape.
          </h1>
          <p className="mt-6 max-w-2xl font-ibm-plex text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
            SYNQ adapts to the shape of your organization — from a 12-person startup to a multi-thousand-person enterprise.
          </p>
        </div>
      </section>

      <section className="border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20">
          <div className="grid md:grid-cols-2 gap-5">
            {SOLUTIONS.map((s) => (
              <div key={s.id} id={s.id} data-testid={`solution-${s.id}`}
                className="scroll-mt-20 rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8">
                <div className="w-11 h-11 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 flex items-center justify-center mb-5">
                  <s.Icon className="w-5 h-5" />
                </div>
                <h3 className="font-outfit text-2xl font-black text-slate-900 dark:text-white">{s.name}</h3>
                <p className="font-ibm-plex text-xs uppercase tracking-widest text-slate-500 mt-1 mb-4">{s.tag}</p>
                <p className="font-ibm-plex text-slate-700 dark:text-slate-300 leading-relaxed">{s.body}</p>
                <ul className="mt-5 space-y-2">
                  {s.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2 font-ibm-plex text-sm text-slate-600 dark:text-slate-400">
                      <span className="text-slate-400 mt-1.5 w-1 h-1 rounded-full bg-slate-400 flex-shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <Link to={`/contact?intent=demo&product=SYNQ+Projects`}
                  className="inline-flex items-center gap-1.5 mt-6 font-outfit font-semibold text-sm text-slate-900 dark:text-white hover:gap-2 transition-all">
                  Talk to us <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
